import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * 4-step progress tracker used across the booking flow:
 *   Trip Details -> Vehicle -> Review -> Payment
 *
 * Refined for the Sydney James luxury design language — thin hairline track,
 * champagne-gold fill, restrained active glow, italic Cormorant numbering.
 */
export const BOOKING_STEPS = [
  { key: "trip", label: "Trip Details" },
  { key: "vehicle", label: "Vehicle" },
  { key: "review", label: "Review" },
  { key: "payment", label: "Payment" },
];

export default function StepProgress({ current = 1, onStepClick = null, dark = false }) {
  const t = dark
    ? {
        active: "text-warm-white",
        done: "text-warm-white/70",
        pending: "text-warm-white/30",
        track: "bg-white/10",
        fill: "bg-gold",
        pendingDot: "bg-transparent text-warm-white/40 border-white/20",
        doneDot: "bg-transparent text-gold border-gold",
        activeDot: "bg-warm-white text-midnight border-warm-white",
      }
    : {
        active: "text-charcoal",
        done: "text-muted-light",
        pending: "text-charcoal/25",
        track: "bg-charcoal/10",
        fill: "bg-gold",
        pendingDot: "bg-transparent text-charcoal/40 border-charcoal/20",
        doneDot: "bg-transparent text-gold border-gold",
        activeDot: "bg-midnight text-warm-white border-midnight",
      };

  return (
    <div data-testid="step-progress" className="w-full">
      <div className="relative flex justify-between items-start gap-2">
        {/* Track sits at the vertical centre of the dot row */}
        <div className={cn("absolute left-3.5 right-3.5 top-3.5 h-px", t.track)} />
        <div
          className={cn("absolute left-3.5 top-3.5 h-px transition-all duration-700", t.fill)}
          style={{
            width: `calc((100% - 1.75rem) * ${Math.min(1, (current - 1) / (BOOKING_STEPS.length - 1))})`,
          }}
        />
        {BOOKING_STEPS.map((step, idx) => {
          const n = idx + 1;
          const isDone = n < current;
          const isActive = n === current;
          const clickable = !!onStepClick && isDone;
          return (
            <button
              key={step.key}
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onStepClick(n)}
              data-testid={`step-${step.key}`}
              data-active={isActive ? "true" : "false"}
              className={cn(
                "relative z-10 flex flex-col items-center gap-3 group",
                clickable ? "cursor-pointer" : "cursor-default",
              )}
            >
              <span
                className={cn(
                  "w-7 h-7 rounded-full grid place-items-center border transition-all duration-500",
                  isActive && cn(t.activeDot, "shadow-[0_0_0_4px_rgba(212,175,55,0.15)]"),
                  isDone && t.doneDot,
                  !isDone && !isActive && t.pendingDot,
                )}
              >
                {isDone ? (
                  <Check className="w-3.5 h-3.5" strokeWidth={2} />
                ) : (
                  <span
                    className="font-display italic text-[13px] leading-none"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    {n}
                  </span>
                )}
              </span>
              <span
                className={cn(
                  "text-[10px] tracking-[0.24em] uppercase font-medium hidden sm:block transition-colors duration-500",
                  isActive ? t.active : isDone ? t.done : t.pending,
                )}
              >
                {step.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
